import { useEffect, useState } from 'react'

import classNames from 'classnames'

import Container from '~/components/atoms/Container/Container'
import Fixture from '~/components/atoms/Fixture/Fixture'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { ComponentTheme } from '~/components/themes'

import ArtworkDataChart from './ArtworkDataChart'

import styles from './ArtworkData.module.css'

type Artist = {
  id: string
  name: string
}

export interface ArtworkDataProps {
  testID?: string
  artworkArtists: Artist[]
  artworkTitle: string
  artworkBody: string
  moduleId?: string
  collectionData: any[]
  theme?: ComponentTheme
}

export default function ArtworkData({
  testID,
  artworkTitle,
  artworkBody,
  artworkArtists,
  moduleId,
  collectionData,
  theme,
}: ArtworkDataProps) {
  const [artistCollectionData, setArtistCollectionData] = useState<any | null>(
    []
  )

  // formalise the data to be selectable by isVisible flag
  useEffect(() => {
    if (collectionData) {
      const newCollectionData = [...collectionData]
      const sortedData: any[] = []
      newCollectionData.forEach((item, key) => {
        const newItem = {
          ...item,
          data: item.data
            ? item.data.sort((a: any, b: any) => a[0] - b[0])
            : [],
          isVisible: item.data && item.data.length > 0 && key < 3,
        }
        sortedData.push(newItem)
      })
      setArtistCollectionData(sortedData)
    }
  }, [collectionData])

  const selectArtist = async (id: string) => {
    const artistId = id.split('-')[1]
    const _artistCollectionData = [...artistCollectionData]
    const existingItem = _artistCollectionData.find(
      artist => artist.artistId === artistId
    )
    const existingItemIndex = _artistCollectionData.findIndex(
      artist => artist.artistId === artistId
    )

    let isVisible = !existingItem.isVisible
    if (_artistCollectionData.filter(x => x.isVisible).length === 1) {
      isVisible = true
    }

    const newItem = {
      ...existingItem,
      isVisible: isVisible,
    }

    _artistCollectionData[existingItemIndex] = newItem
    setArtistCollectionData(_artistCollectionData)
  }

  const getActiveClass = (id: string) => {
    const artistId = id.split('-')[1]

    const activeFound = artistCollectionData.find(
      (item: any) => item.artistId === artistId && item.isVisible === true
    )

    if (activeFound) {
      return styles[`activeHead`]
    }

    return null
  }

  return (
    <div
      className={classNames(styles.base, theme && styles[theme])}
      data-testid={testID}
    >
      <Fixture theme={theme} verticalPadding id={moduleId}>
        <Container>
          <div className={styles.head}>
            {artworkTitle && (
              <Text
                Tag="h3"
                size={TextSizes.primaryTitleLrg}
                className={styles.title}
              >
                {artworkTitle}
              </Text>
            )}
            {artworkBody && (
              <>
                <Text
                  size={TextSizes.secondaryTitleMedReg}
                  className={classNames(
                    styles.body,
                    artworkTitle && styles.bodyWithMargin
                  )}
                >
                  {artworkBody}
                </Text>
              </>
            )}
          </div>
          <div className={styles.chartContainer}>
            <div className={styles.chartHead}>
              {artworkArtists &&
                artworkArtists.map((artist: Artist, key: number) => {
                  const activeClass = getActiveClass(artist.id)
                  const artistId = artist.id.split('-')[1]
                  const artistData =
                    artistCollectionData &&
                    artistCollectionData.find(
                      (item: { artistId: any }) => item.artistId === artistId
                    )

                  // only show artists with data to avoid miss clicks
                  if (artistData && artistData.data.length === 0) return

                  return (
                    <div
                      className={classNames([
                        styles.chartHeadItem,
                        activeClass && activeClass,
                        styles[`chartHeadItem${key + 1}`],
                      ])}
                      key={`${moduleId}-${key}`}
                      onClick={() => selectArtist(artist.id)}
                    >
                      <Text size={TextSizes.primaryTitleSml}>
                        {artist.name}
                      </Text>
                      {activeClass && (
                        <span className={styles.closeBtn}>
                          <span>x</span>
                        </span>
                      )}
                    </div>
                  )
                })}
            </div>
            <div className={styles.chart}>
              {artistCollectionData && (
                <ArtworkDataChart
                  data={artistCollectionData}
                  theme={theme ? theme : ComponentTheme.primary}
                />
              )}
            </div>
          </div>
        </Container>
      </Fixture>
    </div>
  )
}
